/**
 * @file tier-discount.service.ts
 * @description Business logic layer for tier discount bundle widgets.
 * Handles data processing, tier calculation, cart management, and widget state.
 *
 * Tier discount service handles business logic for tier-based discount bundles.
 * Manages bundle data transformation and tier calculations for value-based discounting.
 */

import { ModuleRefImpl } from 'framework/core';
import { Injectable } from 'framework/di';

import { AppService } from '../app/app.service';
import { CartAPI } from '../cart/cart.api';
import { PlatformLoader } from '../platform/platform.loader';
import { TemplateAPI } from '../template/template.api';
import { TierDiscountAPI } from './tier-discount.api';
import {
  AddedItem,
  ExtendedTierDiscountModelProperties,
  TierDiscountModel,
  TierDiscountModelProperties,
  TierDiscountProduct,
  TierDiscountWidgetDesignSettings,
} from './tier-discount.model';
/**
 * Service for managing tier discount bundle business logic.
 *
 * Provides methods for:
 * - Calculating tier achievements and discounts
 * - Managing bundle cart state
 * - Processing product selections
 * - Validating bundle configurations
 *
 * Technical Support teams can extend this class to customize business logic
 * for specific shops without modifying core functionality.
 *
 * @example
 * Extend to add custom tier calculation:
 * ```typescript
 * class CustomTierDiscountService extends TierDiscountService {
 *   protected calculateCurrentTier(model: TierDiscountModel): number {
 *     const baseTier = super.calculateCurrentTier(model);
 *     // Add VIP bonus tier
 *     if (this.isVIPCustomer()) {
 *       return Math.min(baseTier + 1, model.properties.discounts.length);
 *     }
 *     return baseTier;
 *   }
 * }
 * ```
 */
@Injectable
export class TierDiscountService {
  private bundleDataMap = new Map<string, TierDiscountModelProperties>();

  constructor(
    protected moduleRef: ModuleRefImpl,
    protected appService: AppService,
    protected platformLoader: PlatformLoader,
    protected templateAPI: TemplateAPI,
    protected cartAPI: CartAPI,
    protected tierDiscountAPI: TierDiscountAPI
  ) {}

  get widgets() {
    return this.appService.TAEAppConfig?.recommendationWidgets?.['dynamic-bundle'] || {};
  }

  get defaultSettings() {
    return this.appService.TAEAppConfig?.recommendationWidgets?.dynamicBundleDefaultSettings || {};
  }

  getWidgetSettings(widgetId: string): {
    widgetDesignSettings: TierDiscountWidgetDesignSettings;
  } {
    const recommendationWidgets = this.appService.TAEAppConfig?.recommendationWidgets;
    const dynamicBundle = recommendationWidgets?.['dynamic-bundle'];
    const targetWidget = dynamicBundle?.[widgetId];

    const widgetDesignSettings =
      (targetWidget?.widgetDesignSettings as TierDiscountWidgetDesignSettings) || {};

    const defaultSettings = this.defaultSettings;

    // Merge settings: defaultSettings first, then widget-specific settings override
    const mergedSettings = {
      ...defaultSettings,
      ...widgetDesignSettings,
    };

    return { widgetDesignSettings: mergedSettings };
  }

  isValidBundle(widgetId?: string): boolean {
    if (!widgetId || widgetId === '') return false;

    const recommendationWidgets = this.appService.TAEAppConfig?.recommendationWidgets;
    if (!recommendationWidgets || Object.keys(recommendationWidgets).length === 0) return false;

    const dynamicBundleWidgets = recommendationWidgets['dynamic-bundle'];
    if (!dynamicBundleWidgets || Object.keys(dynamicBundleWidgets).length === 0) return false;

    return true;
  }

  /**
   * Fetches bundle data from API
   */
  async fetchBundle(
    widgetId: string,
    params?: { productId?: number }
  ): Promise<TierDiscountModelProperties | undefined> {
    try {
      let bundles: any[] = [];

      if (params?.productId) {
        // Product Page: Use API call
        bundles = await this.tierDiscountAPI.getTierDiscountBundles(params.productId);
      } else {
        // Search Page: Use dynamicBundles from filter context
        bundles = this.tierDiscountAPI.getTierDiscountBundlesFromSearch();
      }

      if (bundles.length === 0) {
        return undefined;
      }

      // Find bundle with bundle_type: 'tiered'
      const tierBundle = bundles.find((bundle) => bundle.bundle_type === 'tiered');
      if (!tierBundle) {
        return undefined;
      }

      // Validate bundle has necessary data
      if (!tierBundle.products || tierBundle.products.length === 0) {
        return undefined;
      }

      // Use widget ID from API response, not the passed widgetId
      const actualWidgetId = tierBundle.widgetId || widgetId;

      return this.processBundleData(tierBundle, actualWidgetId);
    } catch (error) {
      console.error('[TierDiscountService] Error fetching tier bundle:', error);
      return undefined;
    }
  }

  /**
   * Processes bundle data from API response
   */
  protected processBundleData(bundleData: any, widgetId: string): TierDiscountModelProperties {
    const result = this.transformBundleData(bundleData, widgetId);

    // Store processed data
    this.setBundleData(widgetId, result);

    return result;
  }

  getBundleData(widgetId: string): TierDiscountModelProperties | undefined {
    return this.bundleDataMap.get(widgetId);
  }

  setBundleData(widgetId: string, data: TierDiscountModelProperties): void {
    this.bundleDataMap.set(widgetId, data);
  }

  /**
   * Transforms raw bundle data to TierDiscountModelProperties
   */
  protected transformBundleData(bundleData: any, widgetId: string): TierDiscountModelProperties {
    try {
      // Map discounts
      const discounts = ((bundleData.discounts as unknown[]) || [])
        .map((d: unknown) => {
          const discount = d as Record<string, unknown>;
          const discountType = discount.discount_type as string;
          return {
            item: discount.item as number,
            discount_type: (discountType === 'percentage' || discountType === 'amount'
              ? discountType
              : 'percentage') as 'percentage' | 'amount',
            discount_value: discount.discount_value as string,
            achieved: false,
          };
        })
        .sort((a, b) => a.item - b.item);

      // Map products
      const products = ((bundleData.products as unknown[]) || []).map((p: unknown) => {
        const product = p as Record<string, unknown>;
        return {
          id: product.id as number,
          title: product.title as string,
          handle: product.handle as string,
          vendor: product.vendor as string,
          product_type: product.product_type as string,
          tags: (product.tags as string[]) || [],
          variants: ((product.variants as unknown[]) || []).filter(
            (v: unknown) => (v as Record<string, unknown>)?.available !== false
          ),
          images: (product.images as unknown[]) || [],
          images_info: (product.images_info as unknown[]) || [],
          image: product.image as unknown,
          available: product.available as boolean,
          price: (product.price_min || product.price) as number,
          compare_at_price: (product.compare_at_price_min || product.compare_at_price) as number,
          currency: product.currency as string,
          options: (product.options as unknown[]) || [],
          options_with_values: (product.options_with_values as unknown[]) || [],
          addedVariants: [],
        };
      });

      // Get widget design settings from TAEAppConfig like volume bundles do
      const { widgetDesignSettings } = this.getWidgetSettings(widgetId);

      const result: TierDiscountModelProperties = {
        widgetId: (bundleData.widgetId as string) || widgetId,
        bundle_type: 'tiered' as const,
        discount_value: bundleData.discount_value || 0,
        discount_type: bundleData.discount_type || 'percentage',
        bundle_display_type: (bundleData.bundle_display_type as string) || 'bottom',
        threshold_type: bundleData.threshold_type || 'quantity', // New field
        updated_at: bundleData.updated_at || Date.now(),
        excludes: bundleData.excludes || [], // New field
        products: products as unknown as TierDiscountProduct[],
        discounts: discounts as unknown as any[],
        widgetDesignSettings,
        currentTotalQuantity: 0,
        currentTotalValue: 0,
        hasDiscount: false,
        addedItems: [],
      };

      return result;
    } catch (error) {
      console.error('[TierDiscountService] Error in transformBundleData:', error);
      throw error;
    }
  }

  /**
   * Creates TierDiscountModel from properties
   */
  createModel(properties: TierDiscountModelProperties): TierDiscountModel {
    return this.enrichModelWithComputedProperties(properties);
  }

  /**
   * Gets template for widget
   */
  async getTemplate(widgetId: string, templateKey?: string): Promise<string | undefined> {
    const templateName = templateKey || 'tierDiscountBundleBlock';

    // Try tier-discount template first (use templateName, not templateKey)
    const templateFromTAE =
      this.appService.TAEAppConfig.template?.[
        templateName as keyof typeof this.appService.TAEAppConfig.template
      ];
    if (templateFromTAE) {
      return templateFromTAE;
    }

    // Fetch from template API
    const { templateMetadata } = this.appService.TAEAppConfig;
    // Get templateId for tier-discount
    const templateId = templateMetadata?.[templateName as keyof typeof templateMetadata];

    if (!templateId) {
      return undefined;
    }

    try {
      const { templates } = await this.templateAPI.getWidgetTemplate({
        name: templateName,
        id: templateId as string,
      });
      const template = templates[templateId as string];
      if (template) {
        return template;
      }
    } catch (error) {
      console.error(`[TierDiscountService] Error fetching ${templateName}:`, error);
    }

    console.error(`[TierDiscountService] No template found for ${templateName}`);
    return undefined;
  }

  /**
   * Gets cart items from bundle model
   */
  getBundleCartItems(model: TierDiscountModel): Array<{ id: number; quantity: number }> {
    const items: Array<{ id: number; quantity: number }> = [];

    model.properties.products.forEach((product) => {
      product.addedVariants?.forEach((variant) => {
        items.push({
          id: variant.id,
          quantity: variant.quantity,
        });
      });
    });

    return items;
  }

  /**
   * Validates whether a tier discount bundle should be rendered.
   *
   * Checks:
   * - Widget configuration exists
   * - Current page context matches widget settings
   * - Bundle has valid products
   * - Bundle has valid discount tiers
   *
   * @param widgetId - The widget identifier to validate
   * @returns True if bundle should be rendered
   *
   * @example
   * ```typescript
   * if (this.validateTierDiscountWidget('bundle-828365')) {
   *   const model = await this.getTierDiscountModel('bundle-828365');
   *   await this.renderWidget(model);
   * }
   * ```
   */
  validateTierDiscountWidget(widgetId: string): boolean {
    if (!widgetId || !widgetId.trim()) {
      return false;
    }

    // Add shop-specific validation logic here
    return this.performShopValidation(widgetId);
  }

  /**
   * Extension point for shop-specific validation logic.
   * Override to add custom validation rules.
   *
   * @param widgetId - Widget to validate
   * @returns Whether widget passes shop validation
   *
   * @example
   * ```typescript
   * protected performShopValidation(widgetId: string): boolean {
   *   const config = this.getShopConfig();
   *   return config.enabledWidgets.includes(widgetId);
   * }
   * ```
   */
  protected performShopValidation(widgetId: string): boolean {
    return true; // Default: allow all widgets
  }

  /**
   * Fetches and prepares tier discount model for rendering.
   *
   * @param widgetId - Widget identifier
   * @param params - Additional fetch parameters
   * @returns Promise resolving to TierDiscountModel
   */
  async getTierDiscountModel(
    widgetId: string,
    params?: {
      productId?: number;
      collectionId?: number;
    }
  ): Promise<TierDiscountModel | undefined> {
    // Use the updated fetchBundle method instead of old API call
    const bundleData = await this.fetchBundle(widgetId, params);

    if (!bundleData) {
      return undefined;
    }

    return this.enrichModelWithComputedProperties(bundleData);
  }

  /**
   * Enriches model with computed UI properties.
   * Extension point for adding custom computed properties.
   *
   * @param model - Base model from API
   * @returns Model with additional computed properties
   *
   * @example
   * ```typescript
   * protected enrichModelWithComputedProperties(model: TierDiscountModel): TierDiscountModel {
   *   const enriched = super.enrichModelWithComputedProperties(model);
   *   enriched.properties.estimatedSavings = this.calculateEstimatedSavings(enriched);
   *   return enriched;
   * }
   * ```
   */
  protected enrichModelWithComputedProperties(
    properties: TierDiscountModelProperties
  ): TierDiscountModel {
    // Calculate current tier achievement
    const currentTier = this.calculateCurrentTier(properties);

    const hasDiscount = currentTier > 0;
    const currentDiscountPercent = this.calculateDiscountPercent(properties, currentTier);
    const progressPercent = this.calculateProgressPercent(properties);
    const itemsToNextTier = this.calculateItemsToNextTier(properties);

    const extended: ExtendedTierDiscountModelProperties = {
      ...properties,
      hasDiscount,
      currentDiscountPercent,
      currentTier,
      progressPercent,
      itemsToNextTier,
    };

    return new TierDiscountModel(extended);
  }

  /**
   * Calculates which tier is currently achieved based on total quantity.
   * Override for custom tier calculation logic.
   *
   * @param properties - Current model properties
   * @returns Tier index (0 = no tier, 1 = first tier, etc.)
   *
   * @example
   * ```typescript
   * protected calculateCurrentTier(properties: TierDiscountModelProperties): number {
   *   const baseTier = super.calculateCurrentTier(properties);
   *   // Add flash sale bonus
   *   if (this.isFlashSaleActive()) {
   *     return Math.min(baseTier + 1, properties.discounts.length);
   *   }
   *   return baseTier;
   * }
   * ```
   */
  protected calculateCurrentTier(properties: TierDiscountModelProperties): number {
    const {
      currentTotalQuantity = 0,
      currentTotalValue = 0,
      discounts,
      threshold_type = 'quantity',
    } = properties;

    // Use quantity or subtotal based on threshold_type
    const currentValue = threshold_type === 'subtotal' ? currentTotalValue : currentTotalQuantity;

    let achievedTier = 0;
    discounts.forEach((discount, index) => {
      if (currentValue >= discount.item) {
        achievedTier = index + 1;
      }
    });

    return achievedTier;
  }

  /**
   * Calculates discount percentage for current tier.
   * Extension point for custom discount calculation.
   *
   * @param properties - Current model properties
   * @param tierIndex - Current tier index (1-based)
   * @returns Discount percentage
   */
  protected calculateDiscountPercent(
    properties: TierDiscountModelProperties,
    tierIndex: number
  ): number {
    if (tierIndex === 0) return 0;

    const discount = properties.discounts[tierIndex - 1];
    if (!discount) return 0;

    if (discount.discount_type === 'percentage') {
      return parseFloat(discount.discount_value);
    }

    // For fixed discounts, calculate percentage based on current total
    if (properties.currentTotalValue && properties.currentTotalValue > 0) {
      const fixedDiscount = parseFloat(discount.discount_value);
      return (fixedDiscount / properties.currentTotalValue) * 100;
    }

    return 0;
  }

  /**
   * Calculates progress percentage to next tier.
   * Override to customize progress calculation.
   *
   * @param properties - Current model properties
   * @returns Progress percentage (0-100)
   */
  protected calculateProgressPercent(properties: TierDiscountModelProperties): number {
    const {
      currentTotalQuantity = 0,
      currentTotalValue = 0,
      discounts,
      threshold_type = 'quantity',
    } = properties;

    // Use quantity or subtotal based on threshold_type
    const currentValue = threshold_type === 'subtotal' ? currentTotalValue : currentTotalQuantity;

    // Find next tier
    const nextTier = discounts.find((d) => d.item > currentValue);
    if (!nextTier) return 100; // All tiers achieved

    // Find current tier or start
    const currentTierData = discounts
      .slice()
      .reverse()
      .find((d) => d.item <= currentValue);
    const currentTierValue = currentTierData?.item || 0;

    const rangeSize = nextTier.item - currentTierValue;
    const progress = currentValue - currentTierValue;

    return Math.min(100, (progress / rangeSize) * 100);
  }

  /**
   * Calculates items needed to reach next tier.
   *
   * @param properties - Current model properties
   * @returns Number of items needed
   */
  protected calculateItemsToNextTier(properties: TierDiscountModelProperties): number {
    const {
      currentTotalQuantity = 0,
      currentTotalValue = 0,
      discounts,
      threshold_type = 'quantity',
    } = properties;

    // Use quantity or subtotal based on threshold_type
    const currentValue = threshold_type === 'subtotal' ? currentTotalValue : currentTotalQuantity;

    const nextTier = discounts.find((d) => d.item > currentValue);
    if (!nextTier) return 0; // All tiers achieved

    return nextTier.item - currentValue;
  }

  /**
   * Updates bundle state when variant is added.
   * Extension point for custom add logic.
   *
   * @param model - Current model
   * @param variantData - Variant being added
   * @returns Updated model
   *
   * @example
   * ```typescript
   * protected addVariantToBundle(model, variantData) {
   *   const updated = super.addVariantToBundle(model, variantData);
   *   // Track analytics
   *   this.trackVariantAdded(variantData);
   *   return updated;
   * }
   * ```
   */
  addVariantToBundle(
    model: TierDiscountModel,
    variantData: {
      productId: number;
      variantId: number;
      variantTitle: string;
      price: string;
      quantity?: number;
    }
  ): TierDiscountModel {
    const { properties } = model;
    const { productId, variantId, variantTitle, price, quantity = 1 } = variantData;

    // Find product
    const product = properties.products.find((p) => p.id === productId);
    if (!product) return model;

    // Update or add variant
    const existingVariantIndex = product.addedVariants?.findIndex((v) => v.id === variantId) ?? -1;

    if (existingVariantIndex >= 0 && product.addedVariants) {
      // Update quantity
      product.addedVariants[existingVariantIndex].quantity += quantity;
    } else {
      // Add new variant
      if (!product.addedVariants) product.addedVariants = [];
      product.addedVariants.push({
        id: variantId,
        title: variantTitle,
        price,
        quantity,
        productId,
      });
    }

    // Recalculate totals
    return this.recalculateTotals(model);
  }

  /**
   * Updates bundle state when variant quantity changes.
   *
   * @param model - Current model
   * @param variantId - Variant being updated
   * @param quantity - New quantity
   * @returns Updated model
   */
  updateVariantQuantity(
    model: TierDiscountModel,
    variantId: number,
    quantity: number
  ): TierDiscountModel {
    const { properties } = model;

    // Find and update variant
    for (const product of properties.products) {
      if (!product.addedVariants) continue;

      const variant = product.addedVariants.find((v) => v.id === variantId);
      if (variant) {
        if (quantity <= 0) {
          // Remove variant
          product.addedVariants = product.addedVariants.filter((v) => v.id !== variantId);
        } else {
          variant.quantity = quantity;
        }
        break;
      }
    }

    return this.recalculateTotals(model);
  }

  /**
   * Removes variant from bundle.
   *
   * @param model - Current model
   * @param variantId - Variant to remove
   * @returns Updated model
   */
  removeVariantFromBundle(model: TierDiscountModel, variantId: number): TierDiscountModel {
    return this.updateVariantQuantity(model, variantId, 0);
  }

  /**
   * Recalculates bundle totals and tier achievements.
   * Override to customize calculation logic.
   *
   * @param model - Current model
   * @returns Model with updated totals
   *
   * @example
   * ```typescript
   * protected recalculateTotals(model: TierDiscountModel): TierDiscountModel {
   *   const updated = super.recalculateTotals(model);
   *   // Add shipping calculation
   *   updated.properties.shippingCost = this.calculateShipping(updated);
   *   return updated;
   * }
   * ```
   */
  protected recalculateTotals(model: TierDiscountModel): TierDiscountModel {
    const { properties } = model;

    let totalQuantity = 0;
    let totalValue = 0;
    const addedItems: AddedItem[] = [];

    // Calculate from added variants
    properties.products.forEach((product) => {
      product.addedVariants?.forEach((variant) => {
        totalQuantity += variant.quantity;
        const variantPrice = parseFloat(variant.price);
        totalValue += variantPrice * variant.quantity;

        // Add to items list for summary
        addedItems.push({
          id: variant.id,
          title: `${product.title} - ${variant.title}`,
          image: product.images_info[0]?.src || '',
          price: variant.price,
          quantity: variant.quantity,
        });
      });
    });

    // Update tier achievements
    properties.discounts.forEach((discount) => {
      discount.achieved = totalQuantity >= discount.item;
    });

    // Calculate discount
    const currentTier = this.calculateCurrentTier({
      ...properties,
      currentTotalQuantity: totalQuantity,
    });
    const discountPercent = this.calculateDiscountPercent(
      { ...properties, currentTotalQuantity: totalQuantity },
      currentTier
    );
    const discountAmount = (totalValue * discountPercent) / 100;

    properties.currentTotalQuantity = totalQuantity;
    properties.currentTotalValue = totalValue;
    properties.hasDiscount = currentTier > 0;
    properties.addedItems = addedItems;
    properties.originalTotal = totalValue;
    properties.finalTotal = totalValue - discountAmount;

    return this.enrichModelWithComputedProperties(properties);
  }

  /**
   * Adds bundle to Shopify cart.
   * Extension point for custom cart logic.
   *
   * @param model - Model containing items to add
   * @returns Promise resolving when cart updated
   *
   * @example
   * ```typescript
   * async addBundleToCart(model: TierDiscountModel): Promise<void> {
   *   // Add pre-cart validation
   *   if (!this.validateInventory(model)) {
   *     throw new Error('Insufficient inventory');
   *   }
   *   await super.addBundleToCart(model);
   *   // Track conversion
   *   this.trackBundlePurchase(model);
   * }
   * ```
   */
  async addBundleToCart(model: TierDiscountModel): Promise<void> {
    const { properties } = model;

    // Prepare cart items
    const items: Array<{
      id: number;
      quantity: number;
      properties?: Record<string, unknown>;
    }> = [];

    properties.products.forEach((product) => {
      product.addedVariants?.forEach((variant) => {
        items.push({
          id: variant.id,
          quantity: variant.quantity,
          properties: {
            _bundle_id: properties.widgetId,
            _bundle_type: 'tier_discount',
          },
        });
      });
    });

    if (items.length === 0) {
      throw new Error('No items to add to cart');
    }

    try {
      // Add the full bundle in a single cart request to avoid partial adds.
      await this.cartAPI.addMultiProductToCart(items);
    } catch {
      throw new Error('Failed to add tier discount bundle to cart');
    }

    // Post-add cart logic
    this.onBundleAddedToCart(model);
  }

  /**
   * Hook called after bundle successfully added to cart.
   * Override for custom post-add logic like analytics or UI updates.
   *
   * @param model - Model that was added
   */
  protected onBundleAddedToCart(model: TierDiscountModel): void {
    // Extension point for shop-specific logic
  }

  /**
   * Filters products based on shop-specific criteria.
   * Common customization point for shops.
   *
   * @param products - Products to filter
   * @param config - Optional filter configuration
   * @returns Filtered products
   *
   * @example
   * ```typescript
   * protected filterBundleProducts(products, config) {
   *   return super.filterBundleProducts(products, config)
   *     .filter(p => p.vendor !== 'Excluded Vendor')
   *     .filter(p => p.inventory_quantity > 5);
   * }
   * ```
   */
  protected filterBundleProducts(
    products: TierDiscountProduct[],
    config?: Record<string, unknown>
  ): TierDiscountProduct[] {
    // Default filtering
    return products.filter((p) => p.available && p.bundle_matched !== false);
  }
}
